import type {
  SlaAggregateDTO,
  SlaEvaluatedItemDTO,
  SlaState,
} from '@deepguide-ai/types'
import { AlertCircle } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@deepguide-ai/ui/popover'
import { cn } from '@/lib/utils'

const SLA_LABEL: Record<SlaState, string> = {
  breached: 'SLA breached',
  needs_attention: 'SLA due soon',
  ok: 'SLA on track',
  paused: 'SLA paused',
  closed: 'SLA closed',
}

const SLA_STYLE: Record<SlaState, string> = {
  breached:
    'border-status-red/30 bg-status-red-bg text-status-red-strong',
  needs_attention:
    'border-status-orange/30 bg-status-orange-bg text-status-orange-strong',
  ok: 'border-status-green/30 bg-status-green-bg text-status-green-strong',
  paused:
    'border-status-blue/30 bg-status-blue-bg text-status-blue-strong',
  closed:
    'border-status-gray/30 bg-status-gray-bg text-status-gray-strong',
}

function breachedCount(aggregate?: SlaAggregateDTO | null): number {
  return aggregate?.state === 'breached' ? aggregate.breachedCount : 0
}

export function hasBreachedSla(aggregate?: SlaAggregateDTO | null): boolean {
  return breachedCount(aggregate) > 0
}

export function hasOpenSla(aggregate?: SlaAggregateDTO | null): boolean {
  return (
    hasBreachedSla(aggregate) ||
    (aggregate?.needsAttentionCount ?? 0) > 0 ||
    ((aggregate?.okCount ?? 0) > 0 && !!aggregate?.nextDeadlineAt)
  )
}

export function SlaBadge({
  aggregate,
  className,
}: {
  aggregate?: SlaAggregateDTO | null
  className?: string
}) {
  const count = breachedCount(aggregate)
  if (count === 0) return null
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 text-[10px] font-medium',
        SLA_STYLE.breached,
        className
      )}
      title={SLA_LABEL.breached}
    >
      <AlertCircle className='h-2.5 w-2.5' />
      {SLA_LABEL.breached}
      <span>{count}</span>
    </span>
  )
}

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toISOString().slice(0, 10)
}

function sourceLabel(sourceType: string): string {
  if (sourceType === 'control_evidence_status_due_date') return 'Test SLA'
  if (sourceType === 'remediation_ticket') return 'Finding SLA'
  return 'SLA'
}

function deadlineCopy(aggregate: SlaAggregateDTO): string | null {
  if (!aggregate.nextDeadlineAt) return null
  const prefix = aggregate.state === 'breached' ? 'Deadline' : 'Next'
  return `${prefix} ${formatDate(aggregate.nextDeadlineAt)}`
}

export function SlaIndicator({
  aggregate,
  items = [],
  className,
  compact = false,
  summaryMode = 'breached',
}: {
  aggregate?: SlaAggregateDTO | null
  items?: SlaEvaluatedItemDTO[]
  className?: string
  compact?: boolean
  summaryMode?: 'breached' | 'open'
}) {
  if (!aggregate) return null
  const breachCount = breachedCount(aggregate)
  const attentionCount = summaryMode === 'open' ? aggregate.needsAttentionCount : 0
  const okCount =
    summaryMode === 'open' && aggregate.nextDeadlineAt ? aggregate.okCount : 0
  if (breachCount === 0 && attentionCount === 0 && okCount === 0) return null

  const breachedItems = items.filter((item) => item.slaState === 'breached')
  const attentionItems = items.filter((item) => item.slaState === 'needs_attention')
  const visibleItems = [...breachedItems, ...attentionItems]
  const copy = deadlineCopy(aggregate)
  const summaries: Array<{
    state: 'breached' | 'needs_attention' | 'ok'
    count: number
  }> = []
  if (breachCount > 0) summaries.push({ state: 'breached', count: breachCount })
  if (attentionCount > 0) summaries.push({ state: 'needs_attention', count: attentionCount })
  if (okCount > 0) summaries.push({ state: 'ok', count: okCount })

  const badges = (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {summaries.map(({ state, count }) => (
        <span
          key={state}
          className={cn(
            'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 text-[10px] font-medium',
            SLA_STYLE[state],
          )}
          title={
            state === aggregate.state && copy
              ? `${SLA_LABEL[state]} · ${copy}`
              : SLA_LABEL[state]
          }
        >
          <AlertCircle className='h-2.5 w-2.5' />
          {SLA_LABEL[state]} {count}
          {!compact && state === aggregate.state && copy ? (
            <span className='text-current/75'>{copy}</span>
          ) : null}
        </span>
      ))}
    </span>
  )

  if (visibleItems.length === 0) return badges

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/25'
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {badges}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-[320px] rounded-md border-border bg-card p-3 shadow-sm'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='mb-2 text-[10px] font-semibold uppercase tracking-[0.5px] text-text-placeholder'>
          SLA items
        </div>
        <div className='space-y-2'>
          {visibleItems.map((item) => (
            <div
              key={`${item.sourceType}:${item.sourceId}`}
              className={cn(
                'rounded-sm border px-2.5 py-2',
                item.slaState === 'breached'
                  ? 'border-status-red/30 bg-status-red-bg'
                  : 'border-status-orange/30 bg-status-orange-bg',
              )}
            >
              <div className='flex items-center justify-between gap-2'>
                <div className='min-w-0 text-[12px] font-medium text-text-secondary'>
                  {sourceLabel(item.sourceType)}
                </div>
                <span
                  className={cn(
                    'inline-flex h-5 shrink-0 items-center rounded-sm border px-1.5 text-[10px] font-medium',
                    SLA_STYLE[item.slaState],
                  )}
                >
                  {SLA_LABEL[item.slaState]}
                </span>
              </div>
              <div className='mt-1 truncate text-[12px] text-text-body'>
                {item.title}
              </div>
              <div className='mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-text-subtle'>
                <span>Triggered {formatDate(item.triggeredAt)}</span>
                <span>Deadline {formatDate(item.deadlineAt)}</span>
                {item.rule?.days ? <span>SLA {item.rule.days} business days</span> : null}
                {item.daysOverdue != null ? <span>{item.daysOverdue}d overdue</span> : null}
                {item.daysRemaining != null ? <span>{item.daysRemaining}d left</span> : null}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function slaStateLabel(state: SlaState): string {
  return SLA_LABEL[state]
}

export function slaStateClassName(state: SlaState): string {
  return SLA_STYLE[state]
}
